import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createAction } from 'utils';

import ContentSections from 'components/base/ContentSections';
import { SaveAndBackButtons } from 'components/base/BottomStepComponent';
import { listItem } from 'components/banners/DisplayItems';

import './CreateLevelDiscountContent.scss';
import LevelBenefits from './LevelBenefits';
import { DISPLAY_ONLINE_STORE_DISCOUNT_TYPE, LanguageConfig, ONLINE_STORE_DISCOUNT_TYPE, hideLevelDiscount } from 'config/CustomEnums';
import ReactHookFormSwitch from 'components/reactHookFormComponents/ReactHookFormSwitch';
import ReactHookFormTextInput from 'components/reactHookFormComponents/ReactHookFormTextInput';
import ReactHookFormTitleWithRadio from 'components/reactHookFormComponents/ReactHookFormTitleWithRadio';
import ReactHookFormMultipleSelector from 'components/reactHookFormComponents/ReactHookFormMultipleSelector';
import BaseMultipleSelectorDropDown from 'components/base/BaseMultipleSelectorDropDown';

function LevelDiscountContent({
  language,
  onlineStoreProductCategories,
  onlineStoreProductSubcategories,
}) {
  const dispatch = useDispatch();
  const {
    setValue,
    watch,
  } = useFormContext();


  const discountNumberFieldName = "discountNumber";
  const isFreeDeliveryOnShopifyFieldName = "freeDeliveryOnShopify";
  const onlineStoreDiscountTypeFieldName = "onlineStoreDiscountType";
  const limitedMinSpendingFieldName = "limitedMinSpending";
  const limitedMinQuantityFieldName = "limitedMinQuantity";
  const limitedCollectionsFieldName = "limitedCollections";
  const limitedProductTagsFieldName = "limitedProductTags";
  const discountCombinationField = "discountCombination";

  const watchOnlineStoreDiscountType = watch(onlineStoreDiscountTypeFieldName);
  const watchDiscountCombination = watch(discountCombinationField);
  const watchIsFreeDeliveryOnShopify = watch(isFreeDeliveryOnShopifyFieldName);
  const watchSourceName = watch('sourceName');

  const combineWithDelivery = watchDiscountCombination?.some((value) => value?.pk === ONLINE_STORE_DISCOUNT_TYPE.DELIVERY)

  useEffect(() => {
    if (!combineWithDelivery && watchIsFreeDeliveryOnShopify) {
      setValue(isFreeDeliveryOnShopifyFieldName, false, { shouldDirty: true })
    }
  }, [combineWithDelivery, setValue, watchIsFreeDeliveryOnShopify])

  if (language !== LanguageConfig.english) {
    return null;
  };
  return (
    <>
      <label className="create-section-title">General</label>

      {listItem('Name', watchSourceName,)}

      {
        hideLevelDiscount
          ? null
          : <>
            <ReactHookFormTextInput
              title="Level discount value"
              valueFieldName={discountNumberFieldName}
              customClass={'level-discount-input'}
              type="number"
              unit={'%'}
              min={1}
              max={100}
            />
            <ReactHookFormTitleWithRadio
              title={"Discount type"}
              valueFieldName={onlineStoreDiscountTypeFieldName}
              defaultValue={ONLINE_STORE_DISCOUNT_TYPE.ORDER}
              options={
                [ONLINE_STORE_DISCOUNT_TYPE.ORDER, ONLINE_STORE_DISCOUNT_TYPE.PRODUCT].map((type) => ({
                  label: DISPLAY_ONLINE_STORE_DISCOUNT_TYPE?.[type] || type,
                  value: type,
                }))
              }
              bottomDescription={watchOnlineStoreDiscountType === ONLINE_STORE_DISCOUNT_TYPE.PRODUCT 
                ? "Only product of specific categories or subcategories will be discounted. Please select below."
                : "The whole transaction will be discounted."
              }
            />
            {watchOnlineStoreDiscountType === ONLINE_STORE_DISCOUNT_TYPE.PRODUCT ?
              <>
                <BaseMultipleSelectorDropDown
                  hasMarginTop={false}
                  filterTitle={`Online store product category`}
                  customSummaryText={'product category'}
                  placeholder='Search by name'
                  source={onlineStoreProductCategories || []}
                  value={watch(limitedCollectionsFieldName)}
                  onChange={(value) => {
                    setValue(limitedCollectionsFieldName, value, { shouldDirty: true });
                  }}
                  filterAction={(search) => {
                    dispatch(createAction("productCategoryList/getAllOnlineStoreCollections")({
                      search
                    }))
                  }}
                  customItem={(item) => item?.title || "-"}
                />
                <BaseMultipleSelectorDropDown
                  hasMarginTop={false}
                  filterTitle={`Online store product subcategory`}
                  customSummaryText={'product subcategory'}
                  placeholder='Search by name'
                  source={onlineStoreProductSubcategories || []}
                  value={watch(limitedProductTagsFieldName)}
                  onChange={(value) => {
                    setValue(limitedProductTagsFieldName, value, { shouldDirty: true });
                  }}
                  filterAction={(search) => {
                    dispatch(createAction("productCategoryList/getAllOnlineStoreTags")({
                      search
                    }))
                  }}
                  customItem={(item) => item?.name || "-"}
                />
              </>
              : null}
            <ReactHookFormTextInput
              title="Minimum spending amount (optional)"
              tips={"The customer must spend at least the following amount to use the level discount."}
              valueFieldName={limitedMinSpendingFieldName}
              type="number"
              per={"HK$ "}
            />
            <ReactHookFormTextInput
              title="Minimum purchase quantity (optional)"
              tips={"The customer must purchase at least a number of items to use the level discount."}
              valueFieldName={limitedMinQuantityFieldName}
              type="number"
              unit={'items'}
              customClass={'level-discount-input'}
            />
            <ReactHookFormMultipleSelector
              title={"Combinations"}
              tips={"Allow the level discount to be used with other types of discounts."}
              sourceData={[
                ONLINE_STORE_DISCOUNT_TYPE.ORDER,
                ONLINE_STORE_DISCOUNT_TYPE.PRODUCT,
                ONLINE_STORE_DISCOUNT_TYPE.DELIVERY,
              ].map((type) => ({
                pk: type,
                name: DISPLAY_ONLINE_STORE_DISCOUNT_TYPE?.[type] || type,
              }))}
              valueFieldName={discountCombinationField}
            />
            <ReactHookFormSwitch
              disabled={!combineWithDelivery}
              title='Shopify - free delivery'
              valueFieldName={isFreeDeliveryOnShopifyFieldName}
            />
          </>
      }
    </>
  );
}

const CreateLevelDiscountContent = ({
  languages,
  onlineStoreProductCategories,
  onlineStoreProductSubcategories,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { getValues, setError, clearErrors } = useFormContext();
  const validate = async () => {
    clearErrors(['discountNumber']);
    const [discountNumber] = getValues(['discountNumber']);
    let isError = false;

    if (
      !discountNumber
      || Number(discountNumber) <= 0
      || Number(discountNumber) > 100
    ) {
      isError = true;
      setError(
        'discountNumber',
        {
          type: 'require',
          message: 'Please provide required/valid quantity.',
        },
        { shouldFocus: true },
      );
    }
    return !isError;
  };

  const getTabs = (language) => {
    const tabs = [
      <LevelDiscountContent
        language={language}
        onlineStoreProductCategories={onlineStoreProductCategories}
        onlineStoreProductSubcategories={onlineStoreProductSubcategories}
      />,
      <LevelBenefits
        language={language}
      />,
    ];
    return tabs;
  };


  return (
    <>
      <ContentSections
        languageTabContent={{
          containers: languages?.map((item) => ({
            container: getTabs(item.code),
            key: item.code,
            title: item.sourceName,
          })),
        }}
        hidePreview
      />
      <SaveAndBackButtons
        saveText={'Save'}
        backAction={() => history.goBack()}
        saveAction={async () => {
          const isValid = await validate();
          if (isValid) {
            dispatch(createAction('levelDiscount/createOrUpdate')(getValues()));
          }
        }}
      />
    </>
  );
}

const mapPropsToState = (state) => ({
  languages: state.language.allList,
  onlineStoreProductCategories: state.productCategoryList.onlineStoreProductCategories,
  onlineStoreProductSubcategories: state.productCategoryList.onlineStoreProductSubcategories,
})

export default connect(mapPropsToState)(CreateLevelDiscountContent);
